import {
  AUTH_EXPIRED_EVENT,
  notifyAuthExpired,
  syncAuthStateFromSession,
} from '../auth-client';
import { getAccessTokenOrNull, getAuthStateSnapshot } from '../auth-store';
import { ENV_CONFIG } from '../env-config';
import { z } from 'zod';

export const CAMUNDA_AUTH_EXPIRED_EVENT = AUTH_EXPIRED_EVENT;

const DEBUG_CAMUNDA_REQUESTS =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_CAMUNDA_REQUESTS === 'true';

let camundaRequestLogSequence = 0;

function getRequestTimingNow(): number {
  return performance.now();
}

function getSafeEndpointPath(endpoint: string): string {
  const withoutQuery = endpoint.split('?')[0] || endpoint || '/';
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

function nextCamundaRequestId(): string {
  camundaRequestLogSequence += 1;
  return `camunda-${camundaRequestLogSequence}`;
}

function logCamundaRequestTiming({
  requestId,
  endpoint,
  status,
  durationMs,
}: {
  requestId: string;
  endpoint: string;
  status: number | 'network_error';
  durationMs: number;
}) {
  if (!DEBUG_CAMUNDA_REQUESTS) {
    return;
  }

  console.debug('[camunda-request]', {
    request_id: requestId,
    endpoint: getSafeEndpointPath(endpoint),
    status,
    duration_ms: Math.round(durationMs),
  });
}

export class CamundaRequestError extends Error {
  readonly status: number;
  readonly endpoint: string;
  readonly url: string;
  readonly body: string;
  readonly statusText: string;

  constructor({
    status,
    statusText,
    endpoint,
    url,
    body,
    message,
  }: {
    status: number;
    statusText: string;
    endpoint: string;
    url: string;
    body: string;
    message?: string;
  }) {
    super(message ?? `HTTP ${status}: ${statusText}`);
    this.name = "CamundaRequestError";
    this.status = status;
    this.endpoint = endpoint;
    this.url = url;
    this.body = body;
    this.statusText = statusText;
  }
}

type CamundaAuthErrorCode = "MISSING_ACCESS_TOKEN" | "UNAUTHORIZED";
type CamundaAuthErrorDetails =
  | string
  | {
      endpoint?: string;
      url?: string;
      body?: string;
      statusText?: string;
    };

const camundaAuthErrorDetailsSchema = z.object({
  endpoint: z.string().optional(),
  url: z.string().optional(),
  body: z.string().optional(),
  statusText: z.string().optional(),
});
const stringSchema = z.string();

export class CamundaAuthError extends CamundaRequestError {
  readonly code: CamundaAuthErrorCode;

  constructor(
    message: string,
    code: CamundaAuthErrorCode,
    status = 401,
    details: CamundaAuthErrorDetails = {},
  ) {
    const parsedObjectDetails = camundaAuthErrorDetailsSchema.safeParse(details);
    const normalizedDetails = parsedObjectDetails.success
      ? parsedObjectDetails.data
      : { endpoint: stringSchema.parse(details) };
    super({
      status,
      statusText: normalizedDetails.statusText ?? "",
      endpoint: normalizedDetails.endpoint ?? "",
      url: normalizedDetails.url ?? normalizedDetails.endpoint ?? "",
      body: normalizedDetails.body ?? "",
      message,
    });
    this.name = "CamundaAuthError";
    this.code = code;
  }
}

export function isCamundaRequestError<TError>(error: TError): error is TError & CamundaRequestError {
  return error instanceof CamundaRequestError;
}

export function isAuthError<TError>(error: TError): boolean {
  return error instanceof CamundaAuthError ||
    (isCamundaRequestError(error) && (error.status === 401 || error.status === 403));
}

function isTaskEndpoint(endpoint: string): boolean {
  return /^\/task\/[^/]+(?:$|\/)/.test(endpoint);
}

function hasExplicitMissingTaskMessage(normalizedBody: string): boolean {
  return normalizedBody.includes('no matching task') ||
    normalizedBody.includes('cannot find task') ||
    normalizedBody.includes('task does not exist') ||
    normalizedBody.includes("task doesn't exist") ||
    normalizedBody.includes('task is null') ||
    normalizedBody.includes('already completed') ||
    normalizedBody.includes('not active') ||
    /\btask\b[^.]*\bdoes not exist\b/.test(normalizedBody) ||
    /\btask with id\b[^.]*\bnot found\b/.test(normalizedBody);
}

function hasConcurrentTaskUpdateMessage(normalizedBody: string): boolean {
  return normalizedBody.includes('optimisticlockingexception') ||
    normalizedBody.includes('updated by another transaction concurrently');
}

export function isTaskNotActiveError<TError>(error: TError): boolean {
  if (!isCamundaRequestError(error) || !isTaskEndpoint(error.endpoint)) {
    return false;
  }

  if (error.status === 404) {
    return true;
  }

  const normalizedBody = error.body.toLowerCase();
  if (
    error.status === 400 &&
    error.endpoint.endsWith('/form') &&
    normalizedBody.includes('cannot get form for task')
  ) {
    return true;
  }

  if (error.status === 400 || error.status === 409) {
    return hasExplicitMissingTaskMessage(normalizedBody);
  }

  return error.status === 500 && hasExplicitMissingTaskMessage(normalizedBody);
}

export function isConcurrentTaskUpdateError<TError>(error: TError): boolean {
  if (!isCamundaRequestError(error) || !isTaskEndpoint(error.endpoint)) {
    return false;
  }

  const normalizedBody = error.body.toLowerCase();
  return error.status === 409 || (error.status === 500 && hasConcurrentTaskUpdateMessage(normalizedBody));
}

const camundaErrorBodySchema = z.object({
  message: z.string().optional(),
  type: z.string().optional(),
});

function parseCamundaErrorBody(body: string): { message?: string; type?: string } | null {
  try {
    const parsed = camundaErrorBodySchema.safeParse(JSON.parse(body));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function isProcessExecutionError<TError>(error: TError): boolean {
  if (!isCamundaRequestError(error) || !isTaskEndpoint(error.endpoint)) {
    return false;
  }

  if (!error.endpoint.endsWith('/complete')) {
    return false;
  }

  const normalizedBody = error.body.toLowerCase();
  return error.status >= 400 && (
    normalizedBody.includes('cannot complete task') ||
    normalizedBody.includes('unknown property used in expression') ||
    normalizedBody.includes('cannot resolve identifier') ||
    normalizedBody.includes('processengineexception')
  );
}

export function getProcessExecutionErrorMessage<TError>(error: TError): string {
  if (!isCamundaRequestError(error)) {
    return 'Camunda no pudo avanzar el proceso. La tarea sigue abierta.';
  }

  const parsed = parseCamundaErrorBody(error.body);
  const message = parsed?.message ?? error.body;
  const missingVariableMatch = message.match(/Cannot resolve identifier '([^']+)'/);
  if (missingVariableMatch?.[1]) {
    return `Falta la variable "${missingVariableMatch[1]}" en la definicion BPMN. La tarea sigue abierta.`;
  }

  const expressionMatch = message.match(/Unknown property used in expression:\s*(\$\{[^}]+\})/);
  if (expressionMatch?.[1]) {
    return `La expresion BPMN ${expressionMatch[1]} fallo al avanzar. La tarea sigue abierta.`;
  }

  return 'Camunda no pudo avanzar el proceso por un error de definicion BPMN. La tarea sigue abierta.';
}

function formatCamundaErrorMessage(status: number, statusText: string, body: string): string {
  const baseMessage = `HTTP ${status}: ${statusText}`;
  const normalizedBody = body.trim();

  if (!normalizedBody) {
    return baseMessage;
  }

  return `${baseMessage} - ${normalizedBody}`;
}

function castCamundaResponse<TValue>(value: Response | string | null | undefined): TValue {
  return value as TValue;
}

async function readTextAsCamundaResponse<TValue>(response: Response): Promise<TValue> {
  return castCamundaResponse<TValue>(await response.text());
}

export class CamundaBaseService {
  protected static readonly BASE_URL = ENV_CONFIG.getCamundaBaseUrl();

  protected static getApiUrl(endpoint: string): string {
    return `${this.BASE_URL}${endpoint}`;
  }

  protected static async getAuthHeaders(): Promise<HeadersInit> {
    const { accessToken, expiresAt } = getAuthStateSnapshot();
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const shouldSyncSession = !accessToken || (expiresAt !== null && expiresAt <= nowInSeconds + 60);

    if (shouldSyncSession) {
      await syncAuthStateFromSession();
    }

    const resolvedAccessToken = getAccessTokenOrNull();
    if (!resolvedAccessToken) {
      notifyAuthExpired("missing_access_token", this.BASE_URL);
      throw new CamundaAuthError(
        "No hay sesión activa. Se requiere iniciar sesión nuevamente.",
        "MISSING_ACCESS_TOKEN",
      );
    }

    return {
      Authorization: `Bearer ${resolvedAccessToken}`,
    };
  }

  private static async fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
    const authHeaders = await this.getAuthHeaders();
    const headers = new Headers(authHeaders);
    headers.set('Content-Type', 'application/json');
    if (options.headers) {
      new Headers(options.headers).forEach((value, key) => {
        headers.set(key, value);
      });
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }

  protected static async fetchWithSessionRecovery(url: string, options: RequestInit): Promise<Response> {
    let response = await this.fetchWithAuth(url, options);
    if (response.status !== 401) {
      return response;
    }

    const sessionRecovered = await syncAuthStateFromSession(-1);
    if (!sessionRecovered) {
      notifyAuthExpired("backend_401", url, true);
      return response;
    }

    response = await this.fetchWithAuth(url, options);
    if (response.status === 401) {
      notifyAuthExpired("backend_401", url, true);
    }
    return response;
  }

  private static async readErrorBody(response: Response): Promise<string> {
    try {
      return await response.text();
    } catch {
      return "";
    }
  }

  protected static async makeRequest<T>(
    endpoint: string,
    options: RequestInit & {
      rawResponse?: boolean;
      suppressErrorLogStatuses?: number[];
    } = {}
  ): Promise<T> {
    const url = this.getApiUrl(endpoint);
    const { rawResponse, suppressErrorLogStatuses, ...requestOptions } = options;
    const requestId = nextCamundaRequestId();
    const startedAt = getRequestTimingNow();
    let status: number | 'network_error' = 'network_error';

    try {
      const response = await this.fetchWithSessionRecovery(url, requestOptions);
      status = response.status;

      if (!response.ok) {
        const errorBody = await this.readErrorBody(response);

        if (response.status === 401) {
          throw new CamundaAuthError(
            "Sesión expirada o no autorizada en Camunda. Reautenticación requerida.",
            "UNAUTHORIZED",
            401,
            {
              endpoint,
              url,
              body: errorBody,
              statusText: response.statusText,
            },
          );
        }

        if (response.status === 404 && (url.includes('/user/') || endpoint.includes('/user/'))) {
          return castCamundaResponse<T>(null);
        }
        const isVariableNotFound = response.status === 404 && (url.includes('/variables/') || endpoint.includes('/variables/'));
        const isHistoricProcessNotFound =
          response.status === 404 &&
          (url.includes('/history/process-instance/') || endpoint.includes('/history/process-instance/'));
        if (isVariableNotFound) {
          return castCamundaResponse<T>(undefined);
        }
        if (isHistoricProcessNotFound) {
          return castCamundaResponse<T>(null);
        }
        const shouldSuppressErrorLog = suppressErrorLogStatuses?.includes(response.status) ?? false;
        if (!isVariableNotFound && !isHistoricProcessNotFound && !shouldSuppressErrorLog) {
          console.error('Error en la peticion a Camunda', {
            endpoint: getSafeEndpointPath(endpoint),
            status: response.status,
            statusText: response.statusText,
          });
        }

        throw new CamundaRequestError({
          status: response.status,
          statusText: response.statusText,
          endpoint,
          url,
          body: errorBody,
          message: formatCamundaErrorMessage(response.status, response.statusText, errorBody),
        });
      }

      if (rawResponse) {
        return castCamundaResponse<T>(response);
      }

      const contentType = response.headers.get("content-type");
      if (response.status === 204 || !contentType) {
        return castCamundaResponse<T>(undefined);
      }

      if (contentType.includes("application/json")) {
        return response.json();
      }

      return readTextAsCamundaResponse<T>(response);
    } finally {
      logCamundaRequestTiming({
        requestId,
        endpoint,
        status,
        durationMs: getRequestTimingNow() - startedAt,
      });
    }
  }

  protected static async makeRequestNoContent(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<boolean> {
    const url = this.getApiUrl(endpoint);
    const requestId = nextCamundaRequestId();
    const startedAt = getRequestTimingNow();
    let status: number | 'network_error' = 'network_error';

    try {
      const response = await this.fetchWithSessionRecovery(url, options);
      status = response.status;

      if (!response.ok) {
        const errorBody = await this.readErrorBody(response);

        if (response.status === 401) {
          throw new CamundaAuthError(
            "Sesión expirada o no autorizada en Camunda. Reautenticación requerida.",
            "UNAUTHORIZED",
            401,
            {
              endpoint,
              url,
              body: errorBody,
              statusText: response.statusText,
            },
          );
        }
        const errorMessage = errorBody
          ? `HTTP ${response.status}: ${response.statusText} - ${errorBody}`
          : `HTTP ${response.status}: ${response.statusText}`;
        throw new CamundaRequestError({
          status: response.status,
          statusText: response.statusText,
          endpoint,
          url,
          body: errorBody,
          message: errorMessage,
        });
      }

      return true;
    } finally {
      logCamundaRequestTiming({
        requestId,
        endpoint,
        status,
        durationMs: getRequestTimingNow() - startedAt,
      });
    }
  }
}
